import { useState, useContext, useCallback } from "react";
import {
  getSites,
  postSite,
  getSiteDetailsById,
  getSiteUsers,
  updateSiteDetails,
  deleteSiteUser,
  updateSiteUser,
  uploadSiteUsers,
  getSiteFiles,
  uploadSiteFile,
  deleteSiteFile,
  bulkDeleteSiteUsers,
} from "../../../Services/siteServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../Constants";
import { SiteContext } from "../../../Context/SiteContext";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

export const useSites = ({ fetch = true, siteId = null } = {}) => {
  // SITE LIST
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ status: "", search: "" });

  // pagination state for sites
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // site context
  const { site, setSite, clearSite } = useContext(SiteContext);

  // site users
  const [siteUsers, setSiteUsers] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);

 // site files
  const [siteFiles, setSiteFiles] = useState([]);
  const [totalSiteFiles, setTotalSiteFiles] = useState(0);

  const[failedUsers, setFailedUsers] = useState([]);
  const[totalRows, setTotalRows] = useState(0);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const res = await getSites({
        ...filters,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });

      if (res?.status === 200) {
        setSites(res.data.results || []);
        setTotalPages(Math.max(1, Math.ceil((res.data?.count || 0) / rowsPerPage)));
        setError(null);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.SITES_FETCH_FAILED;
        toast.error(msg);
        setError(TOAST_MESSAGES.SITES_FETCH_FAILED);
      }
    } catch (err) {
      setError(TOAST_MESSAGES.SITES_FETCH_FAILED);
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.SITES_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (newPage) => setPage(newPage);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  const createSite = async (data) => {
    setLoading(true);
    try {
      const res = await postSite(data);
      if (res?.status === 201) {
        setSites((prev) => [res.data, ...prev]);
        toast.success(TOAST_MESSAGES.SITES_CREATED);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.SITES_CREATION_ERROR;
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.SITES_CREATION_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateSite = async (id, data) => {
    setLoading(true);
    try {
      const res = await updateSiteDetails(id, data);
      if (res?.status === 200) {
        toast.success(TOAST_MESSAGES.UPDATE_SITE_DETAILS_SUCCESS);
        setSite(res.data);
        setSites((prev) => prev.map((s) => (s.id === id ? res.data : s)));
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.UPDATE_SITE_DETAILS_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.UPDATE_SITE_DETAILS_ERROR;
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteById = async (params) => {
    if(!siteId) return;
    setLoading(true);
    try {
      const res = await getSiteDetailsById(siteId, params);
      if (res?.status === 200) {
        setSite(res.data);
        setError(null);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.SITES_GET_DETAILS_ERROR;
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.SITES_GET_DETAILS_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteUsersList = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * rowsPerPage;
      const res = await getSiteUsers(siteId, { limit: rowsPerPage, offset, search:filters.search });

      if (res?.status === 200) {
        const total = Math.ceil((res.data?.count || 0) / rowsPerPage);
        setSiteUsers(res.data.results || []);
        setTotalPages(Math.ceil((res.data?.count || 0) / rowsPerPage));
        setTotalUserCount(res.data.count || 0);

        // if current page > total pages, go back
        if (page > total && total > 0) {
          setPage(total);
        } else if (total === 0) {
          setPage(1);
        }
      } else {
        setSiteUsers([]);
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USER_FETCH_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      setSiteUsers([]);
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const deleteSiteUserById = async (userId) => {
    try {
      const res = await deleteSiteUser(siteId, userId);
      if (res?.status === 204) {
        toast.success(TOAST_MESSAGES.USER_DELETE_SUCCESS);
        fetchSiteUsersList();
        return true;
      } else {
        toast.error(TOAST_MESSAGES.USER_DELETE_FAILED);
        return false;
      }
    } catch {
      toast.error(TOAST_MESSAGES.USER_DELETE_ERROR);
      return false;
    }
  };

  const updateSiteUserById = async (userId, role) => {
    if(!siteId) return false;
    try {
      const res = await updateSiteUser(siteId, userId, { role });
      if (res?.status === 200) {
        toast.success(TOAST_MESSAGES.USER_UPDATION_SUCCESS);
        fetchSiteUsersList();
        return true;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USER_UPDATION_FAILED;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.USER_UPDATION_ERROR;
      toast.error(msg);
      return false;
    }
  };

  const uploadSiteUsersFile = async (file) => {
    if(!siteId) return;
    try {
      const res = await uploadSiteUsers(siteId, file);
      if (res?.status === 201) {
        toast.success(TOAST_MESSAGES.USERS_UPLOAD_SUCCESS);
      } else if (res.status === 207) {
        setFailedUsers(res.data.skipped_rows || []);
        setTotalRows(res.data.total_rows)
        toast.error(TOAST_MESSAGES.USERS_UPLOAD_PARTIAL);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_UPLOAD_FAILED;
        toast.error(msg);
      }
    } catch(err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USERS_UPLOAD_ERROR;
      toast.error(msg);
    } finally{
      fetchSiteUsersList();
    }
  };

  const fetchSiteFiles = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await getSiteFiles(siteId, {
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });
      if (res.status === 200) {
        setSiteFiles(res.data.results || []);
        setTotalSiteFiles(res.data.count || 0);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
      } else {
        setSiteFiles([]);
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_FETCH_FILE;
        toast.error(msg);
      }
    } catch (err) {
      setSiteFiles([]);
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FAILED_FETCH_FILE;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const uploadSiteFileHandler = async (file) => {
    if (!siteId) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await uploadSiteFile(siteId, formData);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED);
        fetchSiteFiles();
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_UPLOAD_FILE;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FAILED_UPLOAD_FILE;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

   const deleteSiteFileHandler = async (fileId) => {
    if (!siteId) return true;
    setLoading(true);
    try {
      const res = await deleteSiteFile(siteId, fileId);
      if (res.status === 204 || res.status === 200) {
        setSiteFiles((prev) => prev.filter((file) => file.id !== fileId)); // update files list
        setTotalSiteFiles((prev) => Math.max(0, prev - 1)); // decrementing total count
        toast.success(TOAST_MESSAGES.FILE_DELETED);
        return true;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_DELETE_FILE;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FAILED_DELETE_FILE;
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteUsers = async (userIds) => {
    if (!siteId) return false;
    try {
      const res = await bulkDeleteSiteUsers(siteId, userIds);
      if (res.status === 200) {
        toast.success(TOAST_MESSAGES.USERS_DELETE_SUCCESS);
        fetchSiteUsersList(); 
        return true;
      } else if (res.status === 207) {
        toast.success(TOAST_MESSAGES.USERS_DELETE_PARTIAL);
        fetchSiteUsersList();
        return false;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_DELETE_FAILED;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USERS_DELETE_ERROR;
      toast.error(msg);
      return false;
    }
  };

  return {
    // sites
    sites,
    loading,
    setLoading,
    error,
    fetchSites,
    setFilters,
    filters,
    createSite,
    fetchSiteById,
    updateSite,
    site,
    setSite,
    clearSite,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,

    // site users
    siteUsers,
    fetchSiteUsersList,
    deleteSiteUserById,
    updateSiteUserById,
    uploadSiteUsersFile,
    bulkDeleteUsers,
    totalUserCount,

    // files
    siteFiles,
    totalSiteFiles,
    fetchSiteFiles,
    uploadSiteFileHandler,
    deleteSiteFileHandler,

    failedUsers,
    setFailedUsers,
    totalRows,
  };
};
